import { SmartSendSubType } from '../consts/SmartSendSubType';
import {
  getAreaEnabledPreference,
  getAreaSortFieldPreference,
  getAreaSortOrderFieldPreference
} from './DefaultFiltersPreferences';
import { FilterName, SmartSendFilter } from './SmartSendFilterApplier';

export function getTabbedNamedFilter(name: FilterName, filter: SmartSendFilter): SmartSendFilter {
  const named = getNamedFilter(name);
  const updated = {
    ...named,
    SortOrder: filter.SortOrder,
    Skip: filter.Skip,
    Take: filter.Take,
    SearchTerm: filter.SearchTerm
  };

  if (name === 'Groupable') {
    // Use the named
    updated.SortField = named.SortField;
    updated.ShowAsGroup = named.ShowAsGroup;
    updated.MinGroupSize = named.MinGroupSize;
  } else {
    if (filter.Name != 'Groupable') {
      updated.SortField = filter.SortField;
      updated.ShowAsGroup = filter.ShowAsGroup;
      updated.MinGroupSize = filter.MinGroupSize;
    }
  }

  filter.FilterSelections.forEach((s) => {
    if (
      !updated.FilterSelections.find((x) => x.Name === s.Name) &&
      !['Error', 'Status', 'MarketplaceStatus', 'Issues'].includes(s.Name)
    ) {
      updated.FilterSelections.push(s);
    }
  });
  return updated;
}

export function getPartialNamedFilter(name: FilterName, filter: SmartSendFilter): SmartSendFilter {
  return {
    ...getNamedFilter(name),
    SortField: filter.SortField,
    SearchTerm: filter.SearchTerm,
    SortOrder: filter.SortOrder,
    ShowAsGroup: filter.ShowAsGroup,
    Skip: filter.Skip,
    Take: filter.Take
  };
}

export function getBookedSubTypeFilter(subtype: string, take: number, skip: number): SmartSendFilter {
  const f = getNamedFilter('Booked');
  f.FilterSelections = [{ Name: 'SubType', Include: [subtype], Exclude: [] }];
  f.Take = take;
  f.Skip = skip;
  return f;
}

export function getNamedFilter(name: FilterName): SmartSendFilter {
  const basic: SmartSendFilter = {
    SearchTerm: '',
    ShowAsGroup: true,
    SortField: 'Product',
    SortOrder: 'asc',
    FilterSelections: [],
    MinGroupSize: 1,
    Name: name,
    Area: 'DEFAULT',
    Skip: 0,
    Take: 25
  };

  switch (name) {
    case 'InTransit':
      return {
        ...basic,
        SortOrder: getAreaSortOrderFieldPreference('InTransit', 'SortOrder', 'desc'),
        SortField: getAreaSortFieldPreference('InTransit', 'SortField', 'Date'),
        ShowAsGroup: getAreaEnabledPreference('InTransit', 'ShowAsGroup', true),
        Area: 'InTransit',
        FilterSelections: [
          {
            Name: 'MarketplaceStatus',
            Include: ['Dispatched', 'AwaitingDispatch'],
            Exclude: []
          },
          { Name: 'TrackingStatus', Include: ['InTransit'], Exclude: [] }
        ]
      };
    case 'Sold':
      return {
        ...basic,
        SortOrder: getAreaSortOrderFieldPreference(SmartSendSubType, 'SortOrder', 'desc'),
        SortField: getAreaSortFieldPreference(SmartSendSubType, 'SortField', 'Date'),
        ShowAsGroup: getAreaEnabledPreference(SmartSendSubType, 'ShowAsGroup', true),
        Area: SmartSendSubType,
        FilterSelections: [
          {
            Name: 'MarketplaceStatus',
            Include: [SmartSendSubType],
            Exclude: []
          },
          { Name: 'Issues', Include: [], Exclude: [] },
          { Name: 'TrackingStatus', Include: [], Exclude: [] },
          { Name: 'SubType', Include: [SmartSendSubType], Exclude: [] }
        ]
      };
    case 'Ready':
      return {
        ...basic,
        SortOrder: getAreaSortOrderFieldPreference(SmartSendSubType, 'SortOrder', 'asc'),
        SortField: getAreaSortFieldPreference(SmartSendSubType, 'SortField', 'Product'),
        ShowAsGroup: getAreaEnabledPreference(SmartSendSubType, 'ShowAsGroup', true),
        Area: SmartSendSubType,
        FilterSelections: [
          {
            Name: 'MarketplaceStatus',
            Include: [SmartSendSubType],
            Exclude: []
          },
          { Name: 'Issues', Include: [], Exclude: [] },
          { Name: 'Error', Include: ['Ready'], Exclude: [] },
          { Name: 'SubType', Include: [SmartSendSubType], Exclude: [] }
        ]
      };
    case 'SavedForLater':
      return {
        ...basic,
        SortOrder: getAreaSortOrderFieldPreference(SmartSendSubType, 'SortOrder', 'asc'),
        SortField: getAreaSortFieldPreference(SmartSendSubType, 'SortField', 'Product'),
        ShowAsGroup: getAreaEnabledPreference(SmartSendSubType, 'ShowAsGroup', true),
        Area: SmartSendSubType,
        FilterSelections: [
          {
            Name: 'MarketplaceStatus',
            Include: [SmartSendSubType],
            Exclude: []
          },
          { Name: 'Status', Include: ['Inactive'], Exclude: [] },
          { Name: 'SubType', Include: [SmartSendSubType], Exclude: [] }
        ]
      };
    case 'NotReady':
      return {
        ...basic,
        SortOrder: getAreaSortOrderFieldPreference(SmartSendSubType, 'SortOrder', 'asc'),
        SortField: getAreaSortFieldPreference(SmartSendSubType, 'SortField', 'Product'),
        ShowAsGroup: getAreaEnabledPreference(SmartSendSubType, 'ShowAsGroup', true),
        Area: SmartSendSubType,
        FilterSelections: [
          {
            Name: 'MarketplaceStatus',
            Include: [SmartSendSubType],
            Exclude: []
          },
          { Name: 'Issues', Include: [], Exclude: [] },
          { Name: 'Error', Include: [], Exclude: ['Ready'] },
          { Name: 'SubType', Include: [SmartSendSubType], Exclude: [] }
        ]
      };
    case 'Issues':
      return {
        ...basic,
        SortOrder: getAreaSortOrderFieldPreference(SmartSendSubType, 'SortOrder', 'asc'),
        SortField: getAreaSortFieldPreference(SmartSendSubType, 'SortField', 'Product'),
        ShowAsGroup: getAreaEnabledPreference(SmartSendSubType, 'ShowAsGroup', true),
        Area: SmartSendSubType,
        FilterSelections: [
          {
            Name: 'MarketplaceStatus',
            Include: [SmartSendSubType],
            Exclude: []
          },
          { Name: 'Issues', Include: [], Exclude: [] },
          { Name: 'Error', Include: [], Exclude: ['Ready'] },
          { Name: 'SubType', Include: [SmartSendSubType], Exclude: [] }
        ]
      };
    case 'Post_Create':
      return {
        ...basic,
        SortField: 'Date',
        ShowAsGroup: getAreaEnabledPreference(SmartSendSubType, 'ShowAsGroup', true),
        SortOrder: 'desc',
        Area: SmartSendSubType,
        FilterSelections: [
          {
            Name: 'MarketplaceStatus',
            Include: [SmartSendSubType],
            Exclude: []
          },
          { Name: 'SubType', Include: [SmartSendSubType], Exclude: [] }
        ]
      };
    case 'Sold_Mobile':
      return {
        ...basic,
        SortOrder: getAreaSortOrderFieldPreference(SmartSendSubType, 'SortOrder', 'asc'),
        SortField: getAreaSortFieldPreference(SmartSendSubType, 'SortField', 'Product'),
        ShowAsGroup: getAreaEnabledPreference(SmartSendSubType, 'ShowAsGroup', true),
        Area: SmartSendSubType,
        FilterSelections: [
          {
            Name: 'MarketplaceStatus',
            Include: [SmartSendSubType],
            Exclude: []
          },
          { Name: 'TrackingStatus', Include: [], Exclude: [] },
          { Name: 'SubType', Include: [SmartSendSubType], Exclude: [] }
        ]
      };
    case 'Important':
      return {
        ...basic,
        SortOrder: getAreaSortOrderFieldPreference(SmartSendSubType, 'SortOrder', 'asc'),
        SortField: getAreaSortFieldPreference(SmartSendSubType, 'SortField', 'Product'),
        ShowAsGroup: getAreaEnabledPreference(SmartSendSubType, 'ShowAsGroup', true),
        Area: SmartSendSubType,
        FilterSelections: [
          {
            Name: 'MarketplaceStatus',
            Include: [SmartSendSubType],
            Exclude: []
          },
          { Name: 'TrackingStatus', Include: [], Exclude: [] },
          { Name: 'Tag', Include: ['Important'], Exclude: [] },
          { Name: 'SubType', Include: [SmartSendSubType], Exclude: [] }
        ]
      };
    case 'Booked':
      return {
        ...basic,
        SortOrder: getAreaSortOrderFieldPreference('Booked', 'SortOrder', 'desc'),
        SortField: getAreaSortFieldPreference('Booked', 'SortField', 'Date'),
        ShowAsGroup: getAreaEnabledPreference('Booked', 'ShowAsGroup', true),
        Area: 'Booked',
        FilterSelections: [{ Name: 'SubType', Include: [], Exclude: [SmartSendSubType] }]
      };
    case 'Groupable':
      return {
        ...basic,
        SortOrder: getAreaSortOrderFieldPreference('Grouped', 'SortOrder', 'asc'),
        SortField: getAreaSortFieldPreference('Grouped', 'SortField', 'Recipient'),
        ShowAsGroup: getAreaEnabledPreference('Grouped', 'ShowAsGroup', true),
        DisableFilter: true,
        DisableSort: true,
        Area: 'Grouped',
        MinGroupSize: 2,
        FilterSelections: [
          {
            Name: 'Status',
            Include: ['Active'],
            Exclude: []
          },
          {
            Name: 'MarketplaceStatus',
            Include: [SmartSendSubType],
            Exclude: []
          },
          { Name: 'SubType', Include: [SmartSendSubType], Exclude: [] }
        ]
      };
    default:
      return {
        ...basic,
        SortOrder: getAreaSortOrderFieldPreference(SmartSendSubType, 'SortOrder', 'desc'),
        SortField: getAreaSortFieldPreference(SmartSendSubType, 'SortField', 'Date'),
        ShowAsGroup: getAreaEnabledPreference(SmartSendSubType, 'ShowAsGroup', true),
        Area: SmartSendSubType,
        FilterSelections: [
          {
            Name: 'MarketplaceStatus',
            Include: [SmartSendSubType],
            Exclude: []
          },
          {
            Name: 'Status',
            Include: ['Active', 'Inactive'],
            Exclude: []
          },
          { Name: 'Issues', Include: [], Exclude: [] },
          { Name: 'SubType', Include: [SmartSendSubType], Exclude: [] }
        ]
      };
  }
}
